import { parseReplayPrivacy } from "@weloop/core/feedback/replay-masking";

import type { AnnouncementChip } from "./announcements";
import type { DeepLinkTarget } from "./deep-link";
import type { FabHandle } from "./fab";
import type { LauncherAnchor } from "./launcher-anchor";
import type { IncomingMessage } from "./messaging";
import type { UnreadItem } from "./preview";
import type { RecordingBarHandle } from "./recording-bar";
import type { UserReplayContext } from "./replay-session";
import type { ScreenshotBarHandle } from "./screenshot-bar";
import type { ReplaySnapshot, SessionReplayRecorder } from "./session-replay";
import type { SurveyEmbed } from "./survey-embed";
import type {
	OnboardingBullet,
	OnboardingCopy,
	OpenOptions,
	WeloopApi,
	WidgetConfig,
} from "./types";

import {
	mountAnnouncementBanner,
	mountAnnouncementChip,
} from "./announcements";
import { createApi } from "./api";
import { capturePage } from "./capture-page";
import { DEFAULTS, fetchConfig, readOnboardingTone } from "./config";
import { parkDeepLinkFromFragment, takeDeepLink } from "./deep-link";
import { mountFab } from "./fab";
import { hostTheme } from "./host-theme";
import {
	resolveFrameLocaleHint,
	resolveLocale,
	setHostStrings,
	toSupportedLocale,
} from "./i18n";
import { mountIframe } from "./iframe";
import { computeLauncherAnchor, followLauncher } from "./launcher-anchor";
import { installMessageListener, postToWidget } from "./messaging";
import { createOnboardingController } from "./onboarding";
import { collectPageOutline } from "./page-outline";
import { collectPageText } from "./page-text";
import { installPageTracker, redactUrl } from "./page-tracker";
import { mountPreview } from "./preview";
import {
	deriveConfigUrl,
	deriveReplayBundleUrl,
	deriveReplayBundleUrlFromConfigUrl,
	extractProjectId,
	findScriptSrc,
	readConfigOrigin,
	readLauncherEnabled,
} from "./project-id";
import { mountRecordingBar } from "./recording-bar";
import { mountScreenshotBar } from "./screenshot-bar";
import {
	beginScreenshotSession,
	clearScreenshotSession,
	readScreenshotSession,
	resolveSessionStorage,
} from "./screenshot-session";
import { startSessionReplay } from "./session-replay";
import { mountSurveyCard } from "./survey-card-mount";
import { createSurveyEmbed } from "./survey-embed";

export interface BootResult {
	readonly api: ReturnType<typeof createApi>["api"];
	// The fetched config URL is returned so the caller can derive a sibling URL
	// for a different project on `switchProject` without re-deriving from the
	// (possibly stale) `<script>` tag.
	readonly configUrl: string;
	// Returns the `open()` the iframe never received because it was not ready
	// yet – an email deep link, consumed once per page, would otherwise be
	// lost with this mount. The caller replays it on the next one.
	readonly dispose: () => OpenOptions | undefined;
}

export interface BootOverrides {
	// Already-parsed config. The browser extension fetches `config.json` from
	// its background worker (a content script's fetch would run under the host
	// page's CSP) and passes the result here; `configUrl` stays required
	// because sibling URLs are derived from it.
	readonly config?: WidgetConfig;
	readonly configUrl?: string;
	// Dev-only: skip building the iframe URL from `orgSlug`/`projectSlug` and
	// point at a same-origin stub instead. Production never sets this – the
	// origin and the slugs come from the per-project config so the runtime
	// has no host-page-controlled inputs to forge.
	readonly iframeUrl?: string;
	// Override the host page's launcher opt-out. Dev and tests set it
	// explicitly; production reads `data-launcher` off the snippet's `<script>`.
	readonly launcher?: boolean;
	readonly projectId?: string;
	// Dev-only: serve the rrweb UMD from an explicit URL instead of deriving
	// it from the snippet's own `<script>` src. The dev server boots from
	// `src/dev.ts` (not `/v1/<id>/widget.js`), so there's no script src to
	// derive a sibling `replay.js` from. Production leaves this unset.
	readonly replayBundleUrl?: string;
}

// Narrow one entry of a `weloop:unread` message's conversation list. The
// widget is trusted, but the message crosses a frame boundary – parse
// defensively so a stale widget build can't crash the host page.
const parseUnreadItem = (raw: unknown): UnreadItem | undefined => {
	if (typeof raw !== "object" || raw === null) {
		return undefined;
	}
	// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- narrowing arbitrary message data to indexable record after typeof === "object" guard
	const entry = raw as Readonly<Record<string, unknown>>;
	const { createdAt } = entry;
	const { feedbackId } = entry;
	if (typeof feedbackId !== "string" || typeof createdAt !== "string") {
		return undefined;
	}
	const { itemCount } = entry;
	return {
		actorEmail:
			typeof entry["actorEmail"] === "string" ? entry["actorEmail"] : null,
		actorName:
			typeof entry["actorName"] === "string" ? entry["actorName"] : null,
		createdAt,
		excerpt: typeof entry["excerpt"] === "string" ? entry["excerpt"] : null,
		feedbackId,
		itemCount: typeof itemCount === "number" && itemCount > 0 ? itemCount : 1,
		kind: typeof entry["kind"] === "string" ? entry["kind"] : null,
		ruleClassification:
			typeof entry["ruleClassification"] === "string"
				? entry["ruleClassification"]
				: null,
		ruleTitle:
			typeof entry["ruleTitle"] === "string" ? entry["ruleTitle"] : null,
		status: typeof entry["status"] === "string" ? entry["status"] : null,
		threadLabel:
			typeof entry["threadLabel"] === "string" ? entry["threadLabel"] : null,
		timeframe:
			typeof entry["timeframe"] === "string" ? entry["timeframe"] : null,
	};
};

// The thread a recording request came from. Both fields are optional – the
// composer's Session-record button sends no context at all.
const parseReplayContext = (raw: unknown): UserReplayContext | undefined => {
	if (typeof raw !== "object" || raw === null) {
		return undefined;
	}
	// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- narrowing arbitrary message data to indexable record after typeof === "object" guard
	const entry = raw as Readonly<Record<string, unknown>>;
	const { feedbackId, messageId } = entry;
	const context: UserReplayContext = {
		...(typeof feedbackId === "string" ? { feedbackId } : {}),
		...(typeof messageId === "string" ? { messageId } : {}),
	};
	return Object.keys(context).length === 0 ? undefined : context;
};

// One slot of a `weloop:announcements` message. Same defensive stance as
// `parseUnreadItem` – a stale or newer widget build must not crash the host
// page. Unknown kinds pass through: the surface only uses them for accent
// styling, and an unrecognized value falls back to the default accent.
const parseAnnouncementChip = (raw: unknown): AnnouncementChip | undefined => {
	if (typeof raw !== "object" || raw === null) {
		return undefined;
	}
	// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- narrowing arbitrary message data to indexable record after typeof === "object" guard
	const entry = raw as Readonly<Record<string, unknown>>;
	const { id, title } = entry;
	if (typeof id !== "string" || id === "" || typeof title !== "string") {
		return undefined;
	}
	const { imageUrl } = entry;
	const { lead } = entry;
	return {
		id,
		// Only an http(s) URL may become an `img src` on the host page.
		...(typeof imageUrl === "string" && /^https?:\/\//u.test(imageUrl)
			? { imageUrl }
			: {}),
		kind: typeof entry["kind"] === "string" ? entry["kind"] : "",
		...(typeof lead === "string" ? { lead } : {}),
		requireAcknowledge: entry["requireAcknowledge"] === true,
		title,
	};
};

const parseUnreadItems = (raw: unknown): UnreadItem[] => {
	if (!Array.isArray(raw)) {
		return [];
	}
	const items: UnreadItem[] = [];
	for (const entry of raw) {
		const item = parseUnreadItem(entry);
		if (item !== undefined) {
			items.push(item);
		}
	}
	return items;
};

// Dismissed previews survive reloads through localStorage – without it every
// navigation resurrects a card the user already waved away. Kept per project
// and capped so the host page's storage never grows unbounded.
const DISMISSED_PREVIEWS_CAP = 20;

const dismissedStorageKey = (projectId: string): string =>
	`weloop-preview-dismissed:${projectId}`;

// Storage access can throw on locked-down host pages (sandboxed iframes,
// blocked third-party storage). The card then degrades to per-pageview
// dismissal instead of breaking the boot.
const loadDismissedPreviews = (projectId: string): Map<string, string> => {
	try {
		const raw = window.localStorage.getItem(dismissedStorageKey(projectId));
		if (raw === null) {
			return new Map();
		}
		const parsed: unknown = JSON.parse(raw);
		if (typeof parsed !== "object" || parsed === null) {
			return new Map();
		}
		const entries = Object.entries(parsed).filter(
			(entry): entry is [string, string] => typeof entry[1] === "string",
		);
		return new Map(entries);
	} catch {
		return new Map();
	}
};

const saveDismissedPreviews = (
	projectId: string,
	dismissed: ReadonlyMap<string, string>,
): void => {
	try {
		// The values are the ISO timestamps of the dismissed items, so sorting
		// them keeps the newest dismissals when trimming to the cap.
		const entries = [...dismissed.entries()]
			.toSorted((a, b) => a[1].localeCompare(b[1]))
			.slice(-DISMISSED_PREVIEWS_CAP);
		window.localStorage.setItem(
			dismissedStorageKey(projectId),
			JSON.stringify(Object.fromEntries(entries)),
		);
	} catch {
		// Best effort – dismissal still works for this pageview.
	}
};

const readOptionalText = (value: unknown): string | null =>
	typeof value === "string" && value !== "" ? value : null;

// One bullet of a `weloop:onboarding` message. Same defensive stance as
// `parseUnreadItem`: the widget is trusted, but the message crosses a frame
// boundary, so a stale or newer build must not crash the host page.
export const parseOnboardingBullet = (
	raw: unknown,
): OnboardingBullet | undefined => {
	if (typeof raw !== "object" || raw === null) {
		return undefined;
	}
	// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- narrowing arbitrary message data to indexable record after typeof === "object" guard
	const entry = raw as Readonly<Record<string, unknown>>;
	const { label, tone } = entry;
	if (typeof label !== "string" || label === "") {
		return undefined;
	}
	return {
		label,
		tone: readOnboardingTone(tone),
	};
};

/**
 * The viewer-specific onboarding copy, or undefined when the widget reports
 * nothing to show. The title is required for the same reason it is in the
 * published config: bullets under no heading explain nothing.
 */
export const parseOnboardingCopy = (
	message: Readonly<Record<string, unknown>>,
): OnboardingCopy | undefined => {
	const { title } = message;
	if (typeof title !== "string" || title === "") {
		return undefined;
	}
	const bulletsRaw = message["bullets"];
	const bullets: OnboardingBullet[] = [];
	if (Array.isArray(bulletsRaw)) {
		for (const entry of bulletsRaw) {
			const bullet = parseOnboardingBullet(entry);
			if (bullet !== undefined) {
				bullets.push(bullet);
			}
		}
	}
	return {
		bullets,
		ctaLabel: readOptionalText(message["ctaLabel"]),
		lang: readOptionalText(message["lang"]),
		message: readOptionalText(message["message"]),
		title,
	};
};

// The host's language hint rides along as `?lang=` so the widget's server can
// render the iframe's first paint in that language – the iframe navigation
// carries no session, and third-party cookies are blocked cross-site, so the
// URL is the one signal SSR can read. The widget validates it again. The
// host's light or dark look rides as `?theme=` for the same reason.
// Exported for tests.
export const buildIframeSrc = (
	config: Pick<WidgetConfig, "orgSlug" | "projectSlug" | "widgetOrigin">,
	frameLocaleHint: string | undefined,
	theme?: "dark" | "light",
): string => {
	const url = new URL(
		`/${encodeURIComponent(config.orgSlug)}/${encodeURIComponent(config.projectSlug)}`,
		config.widgetOrigin,
	);
	if (frameLocaleHint !== undefined) {
		url.searchParams.set("lang", frameLocaleHint);
	}
	if (theme !== undefined) {
		url.searchParams.set("theme", theme);
	}
	return url.toString();
};

// The `open()` call a deep-link target maps to.
const openOptionsForTarget = (target: DeepLinkTarget): OpenOptions => {
	switch (target.kind) {
		case "announcement": {
			return { announcementId: target.announcementId, view: "announcements" };
		}
		case "survey": {
			return { surveyId: target.surveyId, view: "survey" };
		}
		case "thread": {
			return { feedbackId: target.feedbackId, view: "thread" };
		}
		case "view": {
			return { view: target.view };
		}
		default: {
			return {};
		}
	}
};

// Open what a notification email linked to, if this page load carries a
// deep link. Consumes the link, so a second boot on the same page opens
// nothing.
const openFromDeepLink = (api: WeloopApi): void => {
	const deepLink = takeDeepLink();
	if (deepLink === undefined) {
		return;
	}
	// `projectSlug` marks the call as a deep link: the host page API drops
	// it (`queue.ts`), so only this path and its replay after a project
	// switch carry one.
	api.open({
		...openOptionsForTarget(deepLink.target),
		...(deepLink.token === undefined ? {} : { linkToken: deepLink.token }),
		projectSlug: deepLink.projectSlug,
	});
};

/**
 * Move focus out of the closing panel frame: back to `opener`, the element
 * focused when the panel opened, or to the launcher when that element is
 * gone or cannot take focus.
 */
export const returnFocus = (
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- Element is an external mutable DOM interface
	frame: Element,
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- Element is an external mutable DOM interface
	opener: Element | undefined,
	focusLauncher: () => void,
): void => {
	if (opener instanceof HTMLElement && opener !== document.body) {
		opener.focus({ preventScroll: true });
	}
	// A detached or unfocusable opener leaves focus in the frame.
	if (document.activeElement === frame) {
		focusLauncher();
	}
};

// How long a host navigation waits before the page text and outline are
// read again. Routers call `pushState` before they render the new route,
// and the walk over the DOM is too costly to run inside the router's call.
const PAGE_SETTLE_MS = 600;

// Tears a mount down newest first. One surface failing to go must not keep
// the others on the page.
// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- the array is read, never changed
const runCleanups = (cleanups: (() => void)[]): void => {
	for (const cleanup of cleanups.toReversed()) {
		try {
			cleanup();
		} catch {
			// Keep removing the rest.
		}
	}
};

const mount = async (
	overrides: BootOverrides,
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- the mount appends each teardown as it creates the surface
	cleanups: (() => void)[],
): Promise<BootResult | undefined> => {
	// Before anything async: a login wall or SPA router may send the tab
	// elsewhere during the config fetch, and the fragment would go with it.
	parkDeepLinkFromFragment();
	const projectId = overrides.projectId ?? extractProjectId();
	if (projectId === null) {
		console.error(
			"[weloop] script src must look like " +
				"`https://js.weloop.ai/v1/<your-project-id>/widget.js` – aborting.",
		);
		return undefined;
	}

	let { configUrl } = overrides;
	let scriptSrc: string | null = null;
	if (configUrl === undefined) {
		scriptSrc = findScriptSrc(projectId);
		if (scriptSrc === null) {
			console.error("[weloop] could not locate own script tag – aborting.");
			return undefined;
		}
		configUrl = deriveConfigUrl(scriptSrc, readConfigOrigin());
	} else {
		scriptSrc = findScriptSrc(projectId);
	}
	const config = overrides.config ?? (await fetchConfig(configUrl));
	if (config === null) {
		return undefined;
	}

	let pendingToken: string | undefined = undefined;
	// Last `open()` call made before the iframe signaled ready. Replayed on
	// `weloop:ready` so view + prefill arrive even when the host calls
	// `open()` synchronously before the bundle finishes loading.
	let pendingOpen: OpenOptions | undefined = undefined;
	// An email deep link waiting for ready. Its own slot: a host `open()` or
	// a launcher click before ready must not replace it, or the thread and
	// its single-use sign-in token are lost. Replayed ahead of `pendingOpen`.
	let pendingDeepLink: OpenOptions | undefined = undefined;
	// A `triggerSurvey()` call made before the iframe signaled ready – one
	// slot, not a queue: repeated raises collapse into a single prompt
	// attempt, and the last key wins.
	let pendingSurveyTrigger: { readonly eventKey: string } | undefined =
		undefined;
	let iframeReady = false;

	// Resolved once and shared by the FAB and iframe so their a11y labels agree.
	// Reassigned when the widget reports the signed-in user's language
	// (`weloop:locale`); surfaces mounted after that point pick it up, and the
	// FAB re-labels itself.
	let locale = resolveLocale(config.locale);
	const frameLocaleHint = resolveFrameLocaleHint(config.locale);
	const theme = hostTheme();

	// Forward binding: backdrop clicks and Escape must close through
	// `api.close()` so subscribers and the FAB aria state stay in sync, but
	// `api` is created after the iframe. The chrome only raises close
	// requests at runtime, by which point the binding is set.
	let requestClose: (() => void) | undefined = undefined;
	let requestCollapsePopup: (() => void) | undefined = undefined;
	let requestCollapseReadingRoom: (() => void) | undefined = undefined;

	const iframe = mountIframe(
		overrides.iframeUrl ?? buildIframeSrc(config, frameLocaleHint, theme),
		locale,
		{
			colorScheme: theme,
			onRequestClose: () => {
				requestClose?.();
			},
			onRequestCollapsePopup: () => {
				requestCollapsePopup?.();
			},
			onRequestCollapseReadingRoom: () => {
				requestCollapseReadingRoom?.();
			},
			// The widget owns the prompt: dismissing it there records the
			// refusal, then posts `weloop:survey-close`, which hides the frame.
			onRequestDismissPopup: () => {
				postToWidget(iframe.element, config.widgetOrigin, {
					type: "weloop:survey-dismiss",
				});
			},
			position: config.fab.position,
		},
	);
	cleanups.push(iframe.dispose);

	// The widget owns the expansion state, so a collapse the host chrome
	// raised (backdrop click, Escape) has to travel back to it: the panel
	// stays up until the widget answers.
	requestCollapsePopup = () => {
		postToWidget(iframe.element, config.widgetOrigin, {
			type: "weloop:survey-collapse",
		});
	};

	// Same contract for the reading room: the widget narrows its own view
	// and then signals the iframe to shrink.
	requestCollapseReadingRoom = () => {
		postToWidget(iframe.element, config.widgetOrigin, {
			type: "weloop:expanded-collapse",
		});
	};

	const sendAuth = (token: string): void => {
		postToWidget(iframe.element, config.widgetOrigin, {
			orgSlug: config.orgSlug,
			pageUrl: redactUrl(window.location.href),
			projectSlug: config.projectSlug,
			token,
			type: "weloop:auth",
		});
	};

	const sendOpen = (options: OpenOptions): void => {
		postToWidget(iframe.element, config.widgetOrigin, {
			options,
			type: "weloop:open",
		});
	};

	// The widget's survey popup mirrors the host surface: a drawer taking
	// over suppresses the prompt. Posted after every display transition.
	const sendDisplayMode = (): void => {
		postToWidget(iframe.element, config.widgetOrigin, {
			mode: iframe.mode(),
			type: "weloop:display-mode",
		});
	};

	const postPage = (isFull: boolean): void => {
		postToWidget(iframe.element, config.widgetOrigin, {
			...(isFull
				? { outline: collectPageOutline(), text: collectPageText() }
				: {}),
			title: document.title,
			type: "weloop:page",
			url: redactUrl(window.location.href),
		});
	};

	let pageTimer: ReturnType<typeof setTimeout> | undefined;
	const sendPageUrl = (): void => {
		clearTimeout(pageTimer);
		postPage(true);
	};

	// Host SPA navigations re-target URL-scoped surveys at once; the page
	// text follows once the new route has rendered. Before ready the frame
	// cannot listen, and `weloop:ready` sends the full context itself.
	cleanups.push(
		installPageTracker(() => {
			if (!iframeReady) {
				return;
			}
			postPage(false);
			clearTimeout(pageTimer);
			pageTimer = setTimeout(sendPageUrl, PAGE_SETTLE_MS);
		}),
		() => {
			clearTimeout(pageTimer);
		},
	);

	// Forward declaration: the FAB's onClick dispatches into `api`, and the
	// `showLauncher`/`hideLauncher` deps below drive the FAB – a cycle. The FAB
	// is mounted after `createApi`, so the deps reach it through this binding,
	// which is set by then (the deps only fire at runtime, never during boot).
	let fab: FabHandle | undefined = undefined;

	// The panel slides out from wherever the draggable bubble currently sits.
	// Re-synced before every open – the user may have dragged the bubble to the
	// other edge since the last one.
	const syncDockSide = (): void => {
		iframe.setDockSide(fab?.currentSide() ?? config.fab.position);
	};

	// Host-intended launcher visibility (`launcher: false`, `hideLauncher()`,
	// `showLauncher()`). Tracked separately from the FAB's actual state because
	// a user recording force-hides the FAB – the Stop bar must be the only way
	// to end the recording – and the FAB is restored to this value afterwards.
	let launcherVisible = overrides.launcher ?? readLauncherEnabled();

	// Active host-page recording-state UI shown when the user clicks the
	// composer's Session-record button. The iframe is closed during the
	// recording so the user can interact with the host page; the bar gives
	// them a Stop control.
	let recordingBar: RecordingBarHandle | undefined;

	// The thread that asked for the recording, remembered past the stop so the
	// widget can tell whether a finished capture belongs to the card it is
	// showing. Undefined for a recording started from the composer.
	let pendingRecordingContext: UserReplayContext | undefined;

	// True when the recording in progress was started on an earlier page of
	// this tab – the widget iframe reloaded with the host page, so its
	// in-memory recording state is gone and has to be rebuilt.
	let resumedRecording = false;

	// Host-page "Screenshot this page" bar, up while the widget waits for a
	// screenshot the user asked to take from an in-thread capture card. Like
	// the recording bar, it closes the panel so the capture shows the page
	// and not the drawer, and it survives host-page navigations through
	// session storage.
	let screenshotBar: ScreenshotBarHandle | undefined;
	let pendingScreenshotContext: UserReplayContext | undefined;
	// The PNG bytes held until the widget claims them – the widget may be
	// reloading with the host page at the moment the capture lands.
	let finishedScreenshot: Uint8Array | undefined;
	// True when the ask was made on an earlier page of this tab.
	let resumedScreenshot = false;
	const screenshotStorage = resolveSessionStorage();

	// Focused element when the panel opened, for the close to return to.
	let opener: Element | undefined;
	// The survey embed needs `emit` and `api.on` needs the embed – break the
	// cycle with a late-bound hook, assigned right after both exist.
	const surveyEmbedRef: { current: SurveyEmbed | undefined } = {
		current: undefined,
	};

	// `api` only knows about the iframe and the FAB binding – keeping the rest
	// of the fab side-effects out avoids a tighter coupling with `mountFab`.
	// FAB aria state is synced via `api.on("open"|"close")` once both are wired.
	// oxlint-disable-next-line typescript-eslint/unbound-method -- `emit` is a closure-bound function, not a class method
	const { api, emit } = createApi({
		close: () => {
			// The frame drops out of the layout once its exit slide ends, and
			// focus inside it would fall to <body>. Hand it back to whatever
			// opened the panel, or to the launcher. Focus already elsewhere on
			// the host page stays where the visitor put it.
			const hadFocus = document.activeElement === iframe.element;
			iframe.close();
			emit("close");
			if (hadFocus) {
				returnFocus(iframe.element, opener, () => fab?.focus());
			}
		},
		hideLauncher: () => {
			launcherVisible = false;
			fab?.setVisible(false);
		},
		identify: (token) => {
			if (iframeReady) {
				sendAuth(token);
			} else {
				pendingToken = token;
			}
		},
		onSubscribe: (event) => {
			if (event === "survey") {
				surveyEmbedRef.current?.onSubscribed();
			}
		},
		open: (options) => {
			const effective = options ?? {};
			if (iframeReady) {
				// Read the page again at the moment the user opens the panel:
				// a single-page app fills its content after the navigation
				// that last reported the page.
				sendPageUrl();
				sendOpen(effective);
			} else if (effective.projectSlug === undefined) {
				pendingOpen = effective;
			} else {
				pendingDeepLink = effective;
			}
			syncDockSide();
			if (!iframe.isOpen()) {
				opener = document.activeElement ?? undefined;
			}
			iframe.open();
			emit("open");
		},
		showLauncher: () => {
			launcherVisible = true;
			// While a recording is in progress the FAB stays hidden – the Stop
			// bar is the only control – so the intent is applied on restore.
			if (recordingBar === undefined) {
				fab?.setVisible(true);
			}
		},
		triggerSurvey: (eventKey) => {
			if (iframeReady) {
				postToWidget(iframe.element, config.widgetOrigin, {
					event: eventKey,
					type: "weloop:survey-trigger",
				});
			} else {
				pendingSurveyTrigger = { eventKey };
			}
		},
	});

	// In-app survey delivery: the widget hands `in_app` surveys to page code
	// through the `survey` event; answers travel back over postMessage. Only
	// referenced from callbacks that run after this line.
	const surveyEmbed = createSurveyEmbed({
		deliver: (handle) => emit("survey", handle),
		mount: (request) =>
			mountSurveyCard({
				...request,
				fallbackLocale: frameLocaleHint,
				theme,
				widgetOrigin: config.widgetOrigin,
			}),
		post: (message) => {
			postToWidget(iframe.element, config.widgetOrigin, message);
		},
	});
	surveyEmbedRef.current = surveyEmbed;
	cleanups.push(surveyEmbed.reset);

	// The recorder lives on the host page so it sees the actual DOM the user
	// is interacting with – the widget iframe is intentionally not recorded.
	// The project setting opts into the silent rolling buffer only;
	// user-triggered recordings (the chatbot asks visitors to record) work
	// regardless, with rrweb loading lazily when a recording starts.
	// After `switchProject` the script tag keeps the original project's src,
	// so the lookup by the new id finds nothing – fall back to the config
	// URL's sibling so recordings keep working on the switched project.
	const replayBundleUrl =
		overrides.replayBundleUrl ??
		(scriptSrc === null
			? deriveReplayBundleUrlFromConfigUrl(configUrl)
			: deriveReplayBundleUrl(scriptSrc));
	let replayRecorder: SessionReplayRecorder | undefined;
	// Forward binding, like `requestClose`: the recorder is created before the
	// handler that stops a recording.
	let requestFinishUserRecording: (() => void) | undefined = undefined;
	if (replayBundleUrl !== undefined) {
		replayRecorder = startSessionReplay(
			config.sessionReplay?.enabled === true ? config.sessionReplay : undefined,
			replayBundleUrl,
			{
				// Runs once a recording is up, by which point the binding is set.
				onUserRecordingFull: () => {
					requestFinishUserRecording?.();
				},
				projectId,
			},
		);
		cleanups.push(replayRecorder.dispose);
	}

	// The snapshot is frozen at the moment the widget is opened, not when
	// the composer asks for it. The user may browse the widget home view,
	// read news, then click "Create feedback" minutes later – by the time
	// the composer mounts, the rolling buffer would have aged out
	// everything they did on the host page before clicking the FAB.
	// Re-snapshotting on every open replaces the prior one so a second
	// feedback in the same page lifetime gets a fresh window.
	let pendingSnapshot: ReplaySnapshot | undefined;

	// Re-reveal the widget after a user recording WITHOUT posting
	// `weloop:open`. The composer stayed mounted (just hidden) while the
	// host-page recording bar was up, so simply un-hiding the iframe brings
	// it back with its state intact – and lets the pending
	// `weloop:user-replay-stopped` message land on the still-mounted
	// recording slot. Routing through `api.open()` here would post
	// `weloop:open`, which the widget treats as "navigate to home",
	// unmounting the composer and dropping the just-captured recording.
	const restoreWidget = (): void => {
		fab?.setVisible(launcherVisible);
		syncDockSide();
		iframe.open();
		emit("open");
	};

	// The finished recording, held until the widget claims it. A recording that
	// spanned a host-page navigation comes back to a freshly reloaded widget
	// whose composer or replay card mounts after the events are ready, so the
	// widget pulls the state instead of relying on the push landing in time.
	let finishedRecording: { durationMs: number; events: Uint8Array } | undefined;

	const postUserRecordingState = (): void => {
		const active = replayRecorder?.activeUserRecording();
		postToWidget(iframe.element, config.widgetOrigin, {
			// Whether a `weloop:user-replay-start` would be honored. The widget's
			// composers prefer this host-page recording – it follows the user
			// across host pages – and fall back to a native capture in the
			// iframe only when the snippet cannot record on demand.
			canRecord: replayRecorder !== undefined,
			context: active?.context ?? pendingRecordingContext,
			durationMs: finishedRecording?.durationMs,
			events: finishedRecording?.events,
			inProgress: active !== undefined,
			startedAt: active?.startedAt,
			type: "weloop:user-replay-state",
		});
	};

	// Abort a recording from the composer's Cancel control. The sink is drained
	// without posting the events back – the user is discarding them – and the
	// rolling buffer keeps running for a later attempt.
	const cancelUserRecording = (): void => {
		if (recordingBar === undefined) {
			return;
		}
		recordingBar.dispose();
		recordingBar = undefined;
		pendingRecordingContext = undefined;
		finishedRecording = undefined;
		if (replayRecorder !== undefined) {
			void replayRecorder.stopUserRecording();
		}
		restoreWidget();
	};

	const finishUserRecording = async (): Promise<void> => {
		if (recordingBar === undefined) {
			return;
		}
		recordingBar.dispose();
		recordingBar = undefined;
		const context = pendingRecordingContext;
		// A recording the user carried across host pages comes back to a widget
		// that reloaded with the page: its composer or replay card is gone, so
		// un-hiding the iframe would land the user on the home view with the
		// capture nowhere to go. Route it back to the thread that asked, and let
		// the remounted card pull the events with `weloop:user-replay-state`.
		if (resumedRecording && context?.feedbackId !== undefined) {
			fab?.setVisible(launcherVisible);
			api.open({ feedbackId: context.feedbackId, view: "thread" });
		} else {
			restoreWidget();
		}
		const result =
			replayRecorder === undefined
				? undefined
				: await replayRecorder.stopUserRecording();
		resumedRecording = false;
		if (result === undefined) {
			pendingRecordingContext = undefined;
			postToWidget(iframe.element, config.widgetOrigin, {
				available: false,
				context,
				type: "weloop:user-replay-stopped",
			});
			return;
		}
		finishedRecording = result;
		postToWidget(iframe.element, config.widgetOrigin, {
			available: true,
			context,
			durationMs: result.durationMs,
			events: result.events,
			type: "weloop:user-replay-stopped",
		});
	};

	requestFinishUserRecording = () => {
		void finishUserRecording();
	};

	// A native (`getDisplayMedia`) recording runs inside the widget iframe –
	// the snippet never touches the media – but the host-page chrome is the
	// same as for an rrweb recording: close the panel, hide the launcher, and
	// put the Stop bar up so the user records the page instead of the drawer.
	// The bar's Stop asks the widget to end its recorder; the widget answers
	// with `weloop:native-recording-stopped` once it has, from either Stop.
	const handleNativeRecordingMessage = (message: IncomingMessage): void => {
		switch (message.type) {
			case "weloop:native-recording-start": {
				// One host-page capture at a time, like the screenshot ask.
				if (recordingBar !== undefined || screenshotBar !== undefined) {
					return;
				}
				const startedAtRaw = message["startedAt"];
				const startedAt =
					typeof startedAtRaw === "number" ? startedAtRaw : Date.now();
				api.close();
				fab?.setVisible(false);
				recordingBar = mountRecordingBar(locale, startedAt, () => {
					postToWidget(iframe.element, config.widgetOrigin, {
						type: "weloop:native-recording-stop",
					});
				});
				return;
			}
			case "weloop:native-recording-stopped": {
				if (recordingBar === undefined) {
					return;
				}
				recordingBar.dispose();
				recordingBar = undefined;
				restoreWidget();
				break;
			}
			default:
		}
	};

	const postScreenshotState = (): void => {
		postToWidget(iframe.element, config.widgetOrigin, {
			bytes: finishedScreenshot,
			context: pendingScreenshotContext,
			inProgress: screenshotBar !== undefined,
			type: "weloop:screenshot-state",
		});
	};

	// Bring the widget back once the screenshot flow ends. An ask carried
	// across host pages comes back to a widget that reloaded with the page,
	// so it is routed to the thread that asked instead of un-hidden in place.
	const restoreAfterScreenshot = (): void => {
		const context = pendingScreenshotContext;
		if (resumedScreenshot && context?.feedbackId !== undefined) {
			fab?.setVisible(launcherVisible);
			api.open({ feedbackId: context.feedbackId, view: "thread" });
		} else {
			restoreWidget();
		}
		resumedScreenshot = false;
	};

	const endScreenshotAsk = (): void => {
		screenshotBar?.dispose();
		screenshotBar = undefined;
		if (screenshotStorage !== undefined) {
			clearScreenshotSession(screenshotStorage);
		}
	};

	const cancelScreenshot = (): void => {
		if (screenshotBar === undefined) {
			return;
		}
		endScreenshotAsk();
		const context = pendingScreenshotContext;
		restoreAfterScreenshot();
		pendingScreenshotContext = undefined;
		postToWidget(iframe.element, config.widgetOrigin, {
			available: false,
			cancelled: true,
			context,
			type: "weloop:screenshot-result",
		});
	};

	// The bar's main button. The bar hides itself before the picker opens so
	// it is never on the frame; a dismissed picker brings it back for another
	// try, anything else ends the ask and reports the outcome to the widget.
	const takeScreenshot = async (): Promise<void> => {
		const bar = screenshotBar;
		if (bar === undefined) {
			return;
		}
		bar.setHidden(true);
		let bytes: Uint8Array | undefined;
		let failed = false;
		try {
			bytes = await capturePage();
		} catch {
			failed = true;
		}
		if (bytes === undefined && !failed) {
			bar.setHidden(false);
			return;
		}
		endScreenshotAsk();
		const context = pendingScreenshotContext;
		finishedScreenshot = bytes;
		restoreAfterScreenshot();
		postToWidget(iframe.element, config.widgetOrigin, {
			available: bytes !== undefined,
			bytes,
			context,
			type: "weloop:screenshot-result",
		});
	};

	const mountScreenshotAsk = (): void => {
		fab?.setVisible(false);
		screenshotBar = mountScreenshotBar(locale, {
			onCancel: cancelScreenshot,
			onTake: () => {
				void takeScreenshot();
			},
		});
	};

	// Every `weloop:screenshot-*` control the widget can send.
	const handleScreenshotMessage = (message: IncomingMessage): void => {
		switch (message.type) {
			case "weloop:screenshot-claimed": {
				finishedScreenshot = undefined;
				pendingScreenshotContext = undefined;
				return;
			}
			case "weloop:screenshot-start": {
				// One host-page capture at a time – a recording bar already up
				// owns the page.
				if (screenshotBar !== undefined || recordingBar !== undefined) {
					postToWidget(iframe.element, config.widgetOrigin, {
						available: false,
						context: message["context"],
						type: "weloop:screenshot-result",
					});
					return;
				}
				finishedScreenshot = undefined;
				pendingScreenshotContext = parseReplayContext(message["context"]);
				resumedScreenshot = false;
				if (screenshotStorage !== undefined) {
					beginScreenshotSession(screenshotStorage, {
						context: pendingScreenshotContext,
						projectId,
						startedAt: Date.now(),
					});
				}
				api.close();
				mountScreenshotAsk();
				return;
			}
			case "weloop:screenshot-state-request": {
				postScreenshotState();
				break;
			}
			default:
		}
	};

	// The two host-page capture flows whose chrome the snippet owns – returns
	// whether the message was consumed.
	const routeCaptureMessage = (message: IncomingMessage): boolean => {
		if (message.type.startsWith("weloop:native-recording-")) {
			handleNativeRecordingMessage(message);
			return true;
		}
		if (message.type.startsWith("weloop:screenshot-")) {
			handleScreenshotMessage(message);
			return true;
		}
		return false;
	};

	// Every `weloop:user-replay-*` control the widget can send. Grouped in one
	// place because they share the recording's host-page state – the bar, the
	// launcher visibility, and the capture waiting to be claimed.
	const handleUserReplayMessage = (message: IncomingMessage): void => {
		switch (message.type) {
			case "weloop:user-replay-cancel": {
				cancelUserRecording();
				return;
			}
			case "weloop:user-replay-claimed": {
				// The widget stored the events – release them so a later mount
				// does not pick the same capture up a second time.
				finishedRecording = undefined;
				pendingRecordingContext = undefined;
				return;
			}
			case "weloop:user-replay-start": {
				if (
					replayRecorder === undefined ||
					recordingBar !== undefined ||
					screenshotBar !== undefined
				) {
					postToWidget(iframe.element, config.widgetOrigin, {
						available: false,
						context: message["context"],
						type: "weloop:user-replay-stopped",
					});
					return;
				}
				finishedRecording = undefined;
				pendingRecordingContext = parseReplayContext(message["context"]);
				resumedRecording = false;
				// The business rule's masking, when a replay gate or a classic
				// form rule asked. A widget too old to send it gets the default.
				const isStarting = replayRecorder.startUserRecording(
					pendingRecordingContext,
					parseReplayPrivacy(message["privacy"]),
				);
				api.close();
				// Closing collapsed the panel back into the bubble; hide it so
				// the Stop bar is the only way to end the recording – a bubble
				// click mid-recording would reopen the widget over the page
				// being captured.
				fab?.setVisible(false);
				const bar = mountRecordingBar(locale, Date.now(), () => {
					void finishUserRecording();
				});
				recordingBar = bar;
				// The rrweb bundle failed to load: end the recording with the same
				// refusal as a busy page, instead of a Stop bar that records nothing.
				void (async () => {
					const isListening = await isStarting;
					if (isListening || recordingBar !== bar) {
						return;
					}
					const context = pendingRecordingContext;
					cancelUserRecording();
					postToWidget(iframe.element, config.widgetOrigin, {
						available: false,
						context,
						type: "weloop:user-replay-stopped",
					});
				})();
				return;
			}
			// A widget that reloaded (host navigation, deploy) rebuilds its
			// recording state from here rather than from a push it was not alive
			// to receive.
			case "weloop:user-replay-state-request": {
				postUserRecordingState();
				return;
			}
			// The host-page Stop bar is the usual control, but the widget can end
			// the recording too – it shows the recording state itself while the
			// bar is up on another page.
			case "weloop:user-replay-stop": {
				void finishUserRecording();
				break;
			}
			default:
		}
	};

	// Forward binding, like `requestClose` above: the launcher mounts before
	// the onboarding surface exists, but hover only fires at runtime, by which
	// point the handler is set.
	let handleLauncherHover: ((hovering: boolean) => void) | undefined =
		undefined;

	// The bars come and go at runtime; one cleanup removes whichever is up.
	cleanups.push(() => {
		recordingBar?.dispose();
		recordingBar = undefined;
		screenshotBar?.dispose();
		screenshotBar = undefined;
	});

	// Opt-out launcher: a host page that drives the widget from its own UI
	// passes `launcher: false` so its button and the default FAB don't both
	// show. The FAB always mounts (so `showLauncher()` can reveal it later); the
	// flag only sets its initial visibility.
	fab = mountFab(
		config.fab,
		locale,
		() => {
			if (iframe.isOpen()) {
				api.close();
			} else {
				api.open();
			}
		},
		launcherVisible,
		(hovering) => {
			handleLauncherHover?.(hovering);
		},
	);
	cleanups.push(fab.dispose);

	requestClose = () => {
		api.close();
	};

	// Resume a recording the user started before navigating here. The recorder
	// already re-opened its sink from session storage; the host-page chrome has
	// to follow – bar back up, launcher hidden – or the user is left recording
	// with no way to stop.
	const resumed = replayRecorder?.activeUserRecording();
	if (resumed !== undefined) {
		resumedRecording = true;
		pendingRecordingContext = resumed.context;
		fab.setVisible(false);
		recordingBar = mountRecordingBar(locale, resumed.startedAt, () => {
			void finishUserRecording();
		});
	}

	// Same for a screenshot ask made before navigating here: put the bar back
	// so the user can still capture this page for the thread that asked.
	const resumeScreenshotAsk = (): void => {
		const resumedAsk =
			screenshotStorage === undefined
				? undefined
				: readScreenshotSession(screenshotStorage);
		if (resumedAsk === undefined || recordingBar !== undefined) {
			return;
		}
		// An ask made for another project stays with that project's widget.
		if (resumedAsk.projectId !== projectId) {
			if (screenshotStorage !== undefined) {
				clearScreenshotSession(screenshotStorage);
			}
			return;
		}
		resumedScreenshot = true;
		pendingScreenshotContext = resumedAsk.context;
		mountScreenshotAsk();
	};
	resumeScreenshotAsk();

	// A thread link from a notification email. The token, if any, rides
	// along to the widget and is never logged or kept here. `open()` buffers
	// it until the iframe reports ready, and the widget's org layout
	// exchanges the token before the project shell navigates.
	openFromDeepLink(api);

	// --- Unread badge + notification preview ----------------------------
	const notificationsConfig = config.notifications ?? DEFAULTS.notifications;

	// Last dismissed preview per conversation, keyed by the newest unread
	// item's timestamp. A later notification in the same conversation carries
	// a newer timestamp, so it re-arms the peek; polls that repeat the same
	// state stay dismissed. Persisted so a reload does not resurrect a pill
	// the user already waved away.
	const dismissedPreviews = loadDismissedPreviews(projectId);
	let lastUnread: { readonly count: number; readonly items: UnreadItem[] } = {
		count: 0,
		items: [],
	};

	const preview = mountPreview(locale, config.fab.position, {
		// "Later" furls the pill by hand. Remembering it keeps the same reply
		// from peeking again on the next poll or the next page load.
		onDismiss: () => {
			const [latest] = lastUnread.items;
			if (latest !== undefined) {
				dismissedPreviews.set(latest.feedbackId, latest.createdAt);
				saveDismissedPreviews(projectId, dismissedPreviews);
			}
		},
		onEscape: () => {
			fab?.focus();
		},
		onMarkRead: () => {
			// The widget owns the mutation – it holds the session. The next poll
			// posts a cleared summary, which drops the badge.
			postToWidget(iframe.element, config.widgetOrigin, {
				type: "weloop:mark-all-read",
			});
		},
		onOpen: (feedbackId) => {
			preview.hide();
			api.open({ feedbackId, view: "thread" });
		},
		onOpenInbox: () => {
			preview.hide();
			api.open({ view: "notifications" });
		},
	});
	cleanups.push(preview.dispose);

	// --- Announcement surfaces -------------------------------------------
	// Latest `weloop:announcements` payload from the widget iframe, plus the
	// ids dismissed in this page lifetime. Dismissals persist server-side
	// (the widget records them per user), so the in-memory set only bridges
	// the round trip – the next surfaces settle stops sending the id.
	let lastAnnouncements: {
		readonly banner: AnnouncementChip | null;
		readonly bubble: AnnouncementChip | null;
	} = { banner: null, bubble: null };
	const dismissedAnnouncements = new Set<string>();

	const dismissAnnouncement = (id: string): void => {
		dismissedAnnouncements.add(id);
		// The widget owns the session – it records the dismissal and re-posts
		// the surfaces without the dismissed announcement.
		postToWidget(iframe.element, config.widgetOrigin, {
			id,
			type: "weloop:announcement-dismiss",
		});
	};

	// "Got it" on a chip whose announcement asks for a confirmation – the
	// widget records it the way its own button would.
	const acknowledgeAnnouncement = (id: string): void => {
		dismissedAnnouncements.add(id);
		postToWidget(iframe.element, config.widgetOrigin, {
			id,
			type: "weloop:announcement-acknowledge",
		});
	};

	// The surfaces hide themselves on dismiss; the widget's re-post fills the
	// freed slot with the runner-up announcement (if any).
	const announcementBanner = mountAnnouncementBanner(locale, {
		onDismiss: dismissAnnouncement,
		onOpen: (id) => {
			api.open({
				...(id === undefined ? {} : { announcementId: id }),
				view: "announcements",
			});
		},
	});
	const announcementChip = mountAnnouncementChip(
		locale,
		config.fab.position,
		{
			onAcknowledge: acknowledgeAnnouncement,
			onDismiss: dismissAnnouncement,
			onOpen: (id) => {
				api.open({
					...(id === undefined ? {} : { announcementId: id }),
					view: "announcements",
				});
			},
		},
		config.primaryColor,
	);
	cleanups.push(announcementBanner.dispose, announcementChip.dispose);

	const activeAnnouncement = (
		slot: AnnouncementChip | null,
	): AnnouncementChip | undefined =>
		slot !== null && !dismissedAnnouncements.has(slot.id) ? slot : undefined;

	// Whether the unread pill currently claims the launcher corner – the
	// announcement chip only draws when the corner is otherwise free.
	const unreadPillEligible = (): boolean => {
		const { count, items } = lastUnread;
		const [latest] = items;
		return (
			notificationsConfig.previews &&
			count > 0 &&
			latest !== undefined &&
			dismissedPreviews.get(latest.feedbackId) !== latest.createdAt
		);
	};

	// Set once the onboarding surface exists; `syncAnnouncements` calls it as
	// the last step of settling the corner slot.
	let settleOnboarding: (() => void) | undefined = undefined;

	// The two surfaces claim different parts of the page, so they yield to
	// different things. The banner docks to the top edge in a band it reserves
	// for itself; only the drawer, which dims the page and takes focus, hides
	// it. The chip shares the launcher corner with the survey popup and the
	// unread pill, so it draws only when that corner is free.
	const syncAnnouncements = (): void => {
		const mode = iframe.mode();
		const banner = activeAnnouncement(lastAnnouncements.banner);
		if (mode !== "drawer" && banner !== undefined) {
			announcementBanner.show(banner);
		} else {
			announcementBanner.hide();
		}
		const bubble = activeAnnouncement(lastAnnouncements.bubble);
		if (mode === "hidden" && bubble !== undefined && !unreadPillEligible()) {
			announcementChip.show(bubble);
		} else {
			announcementChip.hide();
		}
		// Last in the corner's priority order – settled once everything above
		// it has claimed or released the slot. Forward binding: the onboarding
		// surface is built after this function, but only runs after boot.
		settleOnboarding?.();
	};

	// --- Onboarding intro card -------------------------------------------
	// The card explains what this widget is for. It sits at the bottom of the
	// corner slot's priority order: a reply waiting to be read always beats an
	// introduction.
	const onboarding =
		config.onboarding === undefined
			? undefined
			: createOnboardingController({
					config: config.onboarding,
					isSlotFree: () =>
						iframe.mode() === "hidden" &&
						!unreadPillEligible() &&
						activeAnnouncement(lastAnnouncements.bubble) === undefined,
					locale,
					mode: config.fab.mode,
					onCta: () => {
						api.open();
					},
					onSeen: () => {
						// The widget owns the session – it records the view against the
						// user so the card stays gone on their other devices.
						postToWidget(iframe.element, config.widgetOrigin, {
							type: "weloop:onboarding-seen",
						});
					},
					position: config.fab.position,
					projectId,
				});
	settleOnboarding = () => {
		onboarding?.sync();
	};

	if (onboarding !== undefined) {
		cleanups.push(onboarding.dispose);
		// The card answers "what is this?" on hover from here on, so the FAB's
		// generic tooltip stands down.
		fab.setTooltipSuppressed(true);
		handleLauncherHover = (hovering) => {
			onboarding.handleHover(hovering);
		};
	}

	// --- Surfaces follow the launcher ----------------------------------------
	// A dragged launcher carries its surfaces along: they pin next to where it
	// rests instead of the configured corner (see `followLauncher`).

	const previewAnchor = (): LauncherAnchor =>
		computeLauncherAnchor(
			fab?.currentSide() ?? config.fab.position,
			fab?.restingRect(),
			{ height: window.innerHeight, width: window.innerWidth },
		);

	const stopFollowingLauncher = followLauncher(fab, [
		{
			setLauncher: () => {
				preview.setAnchor(previewAnchor());
			},
			setLauncherMoving: preview.setLauncherMoving,
		},
		announcementChip,
		onboarding,
		{ setLauncher: iframe.setLauncher },
	]);
	cleanups.push(stopFollowingLauncher);

	// The launcher corner is a single slot: the drawer wins, then a survey
	// popup, then the notification pill, then the announcement chip, then the
	// intro card. The pill only draws while the iframe is fully hidden and
	// re-emerges from the remembered unread state when the higher-priority
	// surface leaves – nothing ever overlaps.
	const syncPreview = (): void => {
		const { count, items } = lastUnread;
		const [latest] = items;
		if (
			!notificationsConfig.previews ||
			count === 0 ||
			latest === undefined ||
			iframe.mode() !== "hidden" ||
			dismissedPreviews.get(latest.feedbackId) === latest.createdAt
		) {
			preview.hide();
			syncAnnouncements();
			return;
		}
		// Measured fresh on every show – a tab mode moves the launcher, and the
		// pill must peek out of wherever it actually sits.
		preview.setAnchor(previewAnchor());
		preview.show(items, count);
		syncAnnouncements();
	};

	const handleAnnouncements = (
		message: Readonly<Record<string, unknown>>,
	): void => {
		lastAnnouncements = {
			banner: parseAnnouncementChip(message["banner"]) ?? null,
			bubble: parseAnnouncementChip(message["bubble"]) ?? null,
		};
		syncAnnouncements();
	};

	/**
	 * The widget's verdict on the intro card for the signed-in viewer: the copy
	 * in their language, and whether they have already been introduced on any
	 * device. Both matter – the published config only knows the project's
	 * source language and nothing about who is looking.
	 */
	const handleOnboarding = (
		message: Readonly<Record<string, unknown>>,
	): void => {
		onboarding?.handleVerdict(
			message["seen"] === true,
			parseOnboardingCopy(message),
		);
	};

	/**
	 * Host-page chrome the widget drives: the announcement surfaces and the
	 * onboarding card. Dispatched together because they share the corner slot
	 * and settle through the same sync pass.
	 */
	const handleHostSurfaceMessage = (
		message: Readonly<Record<string, unknown>>,
	): void => {
		if (message["type"] === "weloop:onboarding") {
			handleOnboarding(message);
			return;
		}
		handleAnnouncements(message);
	};

	const handleUnread = (message: Readonly<Record<string, unknown>>): void => {
		const countRaw = message["conversationCount"];
		const count = typeof countRaw === "number" && countRaw > 0 ? countRaw : 0;
		fab?.setBadge({ count, mode: notificationsConfig.badge });
		lastUnread = { count, items: parseUnreadItems(message["conversations"]) };
		syncPreview();
	};

	// Survey popup control raised by the widget. The drawer always wins –
	// prompts are ignored while it is open. The popup outranks the
	// notification card: `syncPreview` hides the card while the popup is up
	// and restores it once the survey closes.
	const handleSurveyMessage = (type: string, height: unknown): void => {
		if (type === "weloop:survey-open") {
			if (iframe.mode() === "hidden" && typeof height === "number") {
				syncDockSide();
				iframe.showPopup(height);
				sendDisplayMode();
				syncPreview();
			}
			return;
		}
		if (type === "weloop:survey-resize") {
			if (typeof height === "number") {
				iframe.resizePopup(height);
			}
			return;
		}
		if (iframe.mode() === "popup") {
			iframe.close();
			sendDisplayMode();
			syncPreview();
		}
	};

	api.on("open", () => {
		fab?.setExpanded(true);
		// The widget owns the conversation surface while open – no preview
		// competes with it. Opening the thread marks it read server-side, so
		// the badge clears on the next unread message.
		preview.hide();
		syncAnnouncements();
		sendDisplayMode();
		pendingSnapshot = replayRecorder?.snapshot();
	});
	api.on("close", () => {
		fab?.setExpanded(false);
		sendDisplayMode();
		// A card deferred while the drawer was open may show again now.
		syncPreview();
	});

	/**
	 * The widget navigated away from whatever it was showing – an iframe
	 * reload (deploy, crash recovery) or the authed shell replacing the
	 * anonymous one after an identify handshake. The host still holds the
	 * popup's geometry, so the panel would render inside a survey-sized frame;
	 * drop the orphaned surface instead.
	 */
	const closeOrphanedPopup = (): void => {
		if (iframe.mode() !== "popup") {
			return;
		}
		iframe.close();
		sendDisplayMode();
		syncPreview();
	};

	/**
	 * The widget finished booting. Everything queued while it was loading – the
	 * page context, a pending identify token, a pending `open()` – is replayed
	 * here, and any host-page state the widget cannot know about (an orphaned
	 * popup, a recording in progress) is reconciled.
	 */
	const handleWidgetReady = (): void => {
		iframeReady = true;
		// The initial page context. Sending it only on `weloop:authed` misses
		// widgets restored from a session cookie (no JWT handshake) on hosts that
		// never navigate (static pages), so the chatbot loses the "current page"
		// signal.
		sendPageUrl();
		closeOrphanedPopup();
		// A widget that reloaded under an open panel – a deploy, a crash
		// recovery – starts out believing it is closed.
		sendDisplayMode();
		if (pendingToken !== undefined) {
			sendAuth(pendingToken);
			pendingToken = undefined;
		}
		if (pendingDeepLink !== undefined) {
			sendOpen(pendingDeepLink);
			pendingDeepLink = undefined;
		}
		if (pendingOpen !== undefined) {
			sendOpen(pendingOpen);
			pendingOpen = undefined;
		}
		if (pendingSurveyTrigger !== undefined) {
			postToWidget(iframe.element, config.widgetOrigin, {
				event: pendingSurveyTrigger.eventKey,
				type: "weloop:survey-trigger",
			});
			pendingSurveyTrigger = undefined;
		}
		// A widget that reloaded mid-recording has no idea one is running. Tell
		// it before it renders an idle "Start recording" button over a capture
		// already in progress.
		postUserRecordingState();
		postScreenshotState();
		emit("ready");
	};

	// Survey traffic split out of the main router so its case list does not
	// crowd the switch – returns whether the message was consumed.
	const routeSurveyMessage = (message: IncomingMessage): boolean => {
		switch (message.type) {
			case "weloop:survey-close":
			case "weloop:survey-open":
			case "weloop:survey-resize": {
				handleSurveyMessage(message.type, message["height"]);
				return true;
			}
			case "weloop:survey-embed": {
				surveyEmbed.onSurvey(message["survey"], message["appearance"]);
				return true;
			}
			case "weloop:survey-embed-result": {
				surveyEmbed.onResult(message);
				return true;
			}
			// Theatre mode for a questionnaire the anchored card is too small
			// for. Display mode stays `popup` on the wire – the expansion is a
			// sub-state of the prompt, not a surface of its own.
			case "weloop:survey-expand": {
				iframe.setPopupExpanded(message["expanded"] === true);
				return true;
			}
			// The visitor answered a survey that parks the answer until they
			// sign in: the drawer opens on the widget's sign-in screen. Same
			// path as a launcher click, so the popup gives way as it does then.
			case "weloop:survey-sign-in": {
				restoreWidget();
				return true;
			}
			default: {
				return false;
			}
		}
	};

	// The frame settled on a language for the signed-in user. The payload is
	// narrowed to a locale the snippet has strings for; anything else keeps the
	// current chrome.
	// The frame settled on a language: follow it for the bundled catalog when
	// it is one the bundle carries, and take the frame's own strings for the
	// rest – a Spanish frame relabels the launcher in Spanish either way.
	const applyFrameLocale = (raw: unknown, strings: unknown): void => {
		const stringsChanged = setHostStrings(strings, raw);
		const next = toSupportedLocale(typeof raw === "string" ? raw : undefined);
		if (next !== undefined && next !== locale) {
			locale = next;
		}
		fab?.setLocale(locale, stringsChanged);
	};

	// Close requests split out of the main router, like `routeSurveyMessage` –
	// a host-page Escape and a widget-reported one both fold into `api.close`,
	// just through different entry points on the iframe chrome.
	const routeCloseMessage = (message: IncomingMessage): boolean => {
		switch (message.type) {
			case "weloop:close": {
				api.close();
				return true;
			}
			case "weloop:escape": {
				iframe.escape();
				return true;
			}
			default: {
				return false;
			}
		}
	};

	const removeListener = installMessageListener({
		expectedOrigin: config.widgetOrigin,
		iframe: iframe.element,
		onMessage: (message) => {
			if (routeCloseMessage(message)) {
				return;
			}
			if (routeSurveyMessage(message)) {
				return;
			}
			if (routeCaptureMessage(message)) {
				return;
			}
			switch (message.type) {
				case "weloop:annotate-view": {
					// The screenshot annotator needs room to draw: nearly the
					// whole viewport while it is on screen.
					iframe.setExpanded(message["active"] === true);
					break;
				}
				case "weloop:announcements":
				case "weloop:onboarding": {
					handleHostSurfaceMessage(message);
					break;
				}
				case "weloop:authed": {
					// The authed shell mounted after consuming `weloop:auth`, so
					// re-send the page context it may have missed. A survey popup
					// the login route had open is closed by that route itself on
					// unmount – closing here would race the shell's own prompt,
					// which can open before this message lands.
					sendPageUrl();
					sendDisplayMode();
					break;
				}
				case "weloop:expanded-view": {
					// The announcements and knowledge base reading rooms show
					// three columns: the drawer opens wider, rounded, with a band
					// of page left beside it. Never overlaps the annotator – one
					// is a dialog over the composer, the other a whole tab.
					iframe.setReadingRoom(message["active"] === true);
					break;
				}
				case "weloop:locale": {
					applyFrameLocale(message["locale"], message["strings"]);
					break;
				}
				case "weloop:ready": {
					handleWidgetReady();
					return;
				}
				case "weloop:replay-events-request": {
					void (async () => {
						const snapshot = pendingSnapshot;
						if (snapshot === undefined) {
							postToWidget(iframe.element, config.widgetOrigin, {
								available: false,
								type: "weloop:replay-events-response",
							});
							return;
						}
						postToWidget(iframe.element, config.widgetOrigin, {
							available: true,
							events: await snapshot.encode(),
							type: "weloop:replay-events-response",
							windowMs: snapshot.windowMs,
						});
					})();
					break;
				}
				case "weloop:submit": {
					emit("submit", message["payload"]);
					break;
				}
				case "weloop:thread-view": {
					// The widget signals thread enter/leave so the iframe can
					// widen to the pilot's conversation dimensions.
					iframe.setThreadMode(message["active"] === true);
					break;
				}
				case "weloop:unread": {
					handleUnread(message);
					break;
				}
				case "weloop:user-replay-cancel":
				case "weloop:user-replay-claimed":
				case "weloop:user-replay-start":
				case "weloop:user-replay-state-request":
				case "weloop:user-replay-stop": {
					handleUserReplayMessage(message);
					break;
				}
				default:
			}
		},
	});
	cleanups.push(removeListener);

	return {
		api,
		configUrl,
		dispose: () => {
			runCleanups(cleanups);
			return pendingDeepLink ?? pendingOpen;
		},
	};
};

// Boots one mount. Every surface it creates registers its teardown in
// `cleanups` as soon as it exists, so a throw halfway through (a host page
// enforcing Trusted Types, a DOM API the page broke) removes what was
// already mounted instead of leaving a loading iframe and live listeners.
export const bootstrap = async (
	overrides: BootOverrides = {},
): Promise<BootResult | undefined> => {
	const cleanups: (() => void)[] = [];
	try {
		return await mount(overrides, cleanups);
	} catch (error) {
		runCleanups(cleanups);
		throw error;
	}
};
